/**
 * Budget & Draws API — Scope-of-Work workbook + Draw disbursements.
 *
 * Backend: eastview-os frontend_documentation/budget-scope-of-work-draws.md.
 * The endpoints exist identically under BOTH pipeline prefixes (`/underwriting`
 * and `/loan_acquisitions`), so this is a hook that binds them to the active
 * pipeline via PipelineContext — the same "one component, prefix swapped"
 * pattern as useUnderwritingHook. Uses `losHttp` (Bearer auth interceptor).
 */
import { useMemo } from "react";
import { losHttp } from "../lib/losHttp";
import { usePipeline } from "../pipelines/PipelineContext";

export const useBudgetDrawsApi = () => {
  const { apiPrefix } = usePipeline();

  return useMemo(() => {
    const base = `/${apiPrefix}`;
    const cfg = (logName) => ({ logName });
    return {
      // GET all SOW sheets (one per subject property) + budgetTabEligible.
      getScopeOfWork: (dealId) =>
        losHttp
          .get(`${base}/scope_of_work/${dealId}`, cfg("SOW: get"))
          .then((r) => r.data),

      // PUT a full sheet (body = the ScopeOfWork shape incl. subjectPropertyId +
      // lineItems[], each row carrying its oid/_id; new rows may omit the oid).
      saveScopeOfWork: (dealId, body) =>
        losHttp
          .put(`${base}/scope_of_work/${dealId}`, body, cfg("SOW: save"))
          .then((r) => r.data),

      // POST build sheet(s) from documents. `refresh` overwrites a non-empty sheet.
      populateScopeOfWork: (dealId, { refresh = false } = {}) =>
        losHttp
          .post(
            `${base}/scope_of_work/${dealId}/populate_from_extracts`,
            refresh ? { refresh: true } : {},
            cfg("SOW: populate"),
          )
          .then((r) => r.data),

      // GET every budget the loan holds for a property — budget documents,
      // feasibility (proposed / recommended), appraisal renovation budget — as
      // BudgetSourceSelection rows: { dealId, candidates: [] } (2026-09-22).
      getBudgetSources: (dealId, subjectPropertyId) =>
        losHttp
          .get(`${base}/scope_of_work/${dealId}/budget_sources`, {
            params: subjectPropertyId ? { subjectPropertyId } : {},
            ...cfg("SOW: budget sources"),
          })
          .then((r) => r.data),

      // PUT the FINAL-budget lock: { subjectPropertyId, locked, budgetSource }.
      // `locked: false` with a budgetSource records the source choice without
      // locking. 422 when locking a sheet with no line items. → { dealId, scope }.
      lockBudget: (dealId, body) =>
        losHttp
          .put(`${base}/scope_of_work/${dealId}/lock`, body, cfg("SOW: lock"))
          .then((r) => r.data),

      // GET draws (sorted by drawNumber) + loan-level rollup.
      getDraws: (dealId) =>
        losHttp
          .get(`${base}/draws/${dealId}`, cfg("Draws: get"))
          .then((r) => r.data),

      // POST a new draw — server assigns drawNumber / totalDrawn / holdback, so
      // don't send those. `snapshotScopeOfWork` embeds the current SOW sheet.
      createDraw: (dealId, body) =>
        losHttp
          .post(`${base}/draws/${dealId}`, body, cfg("Draws: create"))
          .then((r) => r.data),

      // PUT update a draw (re-validated; rollups recomputed).
      updateDraw: (dealId, drawId, body) =>
        losHttp
          .put(`${base}/draws/${dealId}/${drawId}`, body, cfg("Draws: update"))
          .then((r) => r.data),

      // DELETE a draw — only the LAST draw (409 otherwise: every later draw's
      // eligible advance was computed on top of it). The draw is archived, not
      // destroyed. → { deletedDrawId, deletedDrawNumber, archiveId, rollup }.
      deleteDraw: (dealId, drawId) =>
        losHttp
          .delete(`${base}/draws/${dealId}/${drawId}`, cfg("Draws: delete"))
          .then((r) => r.data),

      // POST site-inspection report(s), multipart `uploadFiles` + optional
      // `drawId` (the draw the report backs). Parsed by the listener; returns
      // { documents, status: "parsing" } at once (2026-10-02).
      importInspections: (dealId, files, drawId) => {
        const form = new FormData();
        (files || []).forEach((f) => form.append("uploadFiles", f));
        if (drawId) form.append("drawId", drawId);
        return losHttp
          .post(`${base}/draws/${dealId}/inspections`, form, cfg("Draws: import inspection"))
          .then((r) => r.data);
      },

      // GET the inspection review list, newest upload first. Poll while any row
      // is `parsing`.
      getInspections: (dealId) =>
        losHttp
          .get(`${base}/draws/${dealId}/inspections`, cfg("Draws: inspections"))
          .then((r) => r.data),

      // POST apply a parsed report: { drawId } updates that draw, no drawId
      // creates the next one; any draw field in the body overrides the parsed
      // value. 409 unless the report is `parsed`. → { draw, rollup }.
      applyInspection: (dealId, documentId, body) =>
        losHttp
          .post(
            `${base}/draws/${dealId}/inspections/${documentId}/apply`,
            body || {},
            cfg("Draws: apply inspection"),
          )
          .then((r) => r.data),

      // GET the FCI draw-request payload (a builder/viewer — the LOS never posts).
      getDrawFciPayload: (dealId, drawId) =>
        losHttp
          .get(
            `${base}/draws/${dealId}/${drawId}/fci_payload`,
            cfg("Draws: FCI payload"),
          )
          .then((r) => r.data),

      /**
       * GET the report photo reviews for the loan — one `ReportPhotoReview`
       * per extracted feasibility / draw-inspection report, newest first
       * (`{ dealId, reviews: [] }`).
       *
       * Backend `budgets-draws-report-photos.md` (live 2026-09-21). This is the
       * ONLY read route that carries a feasibility report's own facts —
       * `reportVendor`, `reportDate`, `reviewBasis` (site visit vs desk review),
       * `budgetRecommendation` + amount, `totalProposedBudget` /
       * `totalRecommendedBudget`, `statedPercentComplete` and the photo strip.
       * The budget-review rollup those figures also appear in is NOT served
       * anywhere, so this is how the Feasibility section shows real numbers
       * instead of "an extract exists".
       */
      getReportPhotoReviews: (dealId) =>
        losHttp
          .get(
            `${base}/report_photo_reviews/${dealId}`,
            cfg("Report photos: get"),
          )
          .then((r) => r.data),

      /**
       * One stored report photo as an object URL. `imagePath` is the value the
       * read route puts on each photo (its `blobPath` with the
       * `{dealId}-documentextracts/` prefix stripped); a `null` one was
       * catalogued but never stored (a logo, a cover page) and must not be
       * requested. The endpoint only serves report-photo paths — anything else
       * is a 400 — and the bytes need the Bearer header, so this fetches a Blob
       * rather than letting an <img src> hit the URL unauthenticated. CALLERS
       * REVOKE the object URL.
       */
      getReportPhotoUrl: (dealId, imagePath) =>
        losHttp
          .get(`${base}/report_photo_reviews/${dealId}/image`, {
            params: { path: imagePath },
            responseType: "blob",
            ...cfg("Report photos: image"),
          })
          .then((r) => URL.createObjectURL(r.data)),
    };
  }, [apiPrefix]);
};

export default useBudgetDrawsApi;
